// ts-nocheck

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  FormGroup,
  Box,
  Typography,
  Alert,
  Switch
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Project, CreateProjectData } from "../state/atoms";

interface ProjectFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectData) => void;
  project?: Project | null;
  loading?: boolean;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  open,
  onClose,
  onSubmit,
  project,
  loading = false
}) => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors }
  } = useForm<CreateProjectData>({
    defaultValues: {
      name: "",
      website: "",
      twitter: "",
      telegram: "",
      contract_address: "",
      chain: "ETH",
      features: {
        chat: true,
        ai_search: false,
        feedback: false
      },
      telegram_group_id: "",
      is_super_project: false
    }
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        website: project.website || "",
        twitter: project.twitter || "",
        telegram: project.telegram || "",
        contract_address: project.contract_address || "",
        chain:
          (project.chain as
            | "ETH"
            | "SOL"
            | "BNB"
            | "BASE"
            | "ARBITRUM"
            | "POLYGON"
            | "AVALANCHE") || "ETH",
        features: project.features,
        telegram_group_id: project.telegram_group_id || "",
        is_super_project: project.is_super_project || false
      });
    } else {
      reset({
        name: "",
        website: "",
        twitter: "",
        telegram: "",
        contract_address: "",
        chain: "ETH",
        features: {
          chat: true,
          ai_search: false,
          feedback: false
        },
        telegram_group_id: "",
        is_super_project: false
      });
    }
    setError(null);
  }, [project, reset]);

  const handleFormSubmit = (data: CreateProjectData) => {
    try {
      // Remove is_super_project from the data since it shouldn't be updated
      const { is_super_project, ...updateData } = data;
      onSubmit(updateData as CreateProjectData);
      setError(null);
    } catch (err) {
      setError("Failed to save project. Please try again.");
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 768}
      PaperProps={{
        sx: {
          borderRadius: window.innerWidth >= 768 ? 2 : 0,
          m: window.innerWidth >= 768 ? 2 : 0,
          height: window.innerWidth >= 768 ? 'auto' : '100vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          color: "primary.main"
        }}
      >
        {project ? "Edit Project" : "Create New Project"}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ 
          pt: 3,
          maxHeight: { xs: '70vh', sm: 'none' },
          overflow: { xs: 'auto', sm: 'visible' }
        }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2}>
            <Controller
              name="name"
              control={control}
              rules={{ 
                required: "Project name is required",
                minLength: {
                  value: 2,
                  message: "Project name must be at least 2 characters"
                },
                maxLength: {
                  value: 100,
                  message: "Project name must be less than 100 characters"
                }
              }}
              render={({ field }: { field: any }) => (
                <TextField
                  {...field}
                  label="Project Name"
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Controller
                name="website"
                control={control}
                rules={{
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: "Please enter a valid URL starting with http:// or https://"
                  }
                }}
                render={({ field }: { field: any }) => (
                  <TextField
                    {...field}
                    label="Website URL"
                    fullWidth
                    placeholder="https://example.com"
                    error={!!errors.website}
                    helperText={errors.website?.message}
                  />
                )}
              />
              <Controller
                name="twitter"
                control={control}
                rules={{
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: "Please enter a valid URL starting with http:// or https://"
                  }
                }}
                render={({ field }: { field: any }) => (
                  <TextField
                    {...field}
                    label="Twitter URL"
                    fullWidth
                    placeholder="https://twitter.com/username"
                    error={!!errors.twitter}
                    helperText={errors.twitter?.message}
                  />
                )}
              />
            </Box>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Controller
                name="telegram"
                control={control}
                rules={{
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: "Please enter a valid URL starting with http:// or https://"
                  }
                }}
                render={({ field }: { field: any }) => (
                  <TextField
                    {...field}
                    label="Telegram URL"
                    fullWidth
                    placeholder="https://t.me/groupname"
                    error={!!errors.telegram}
                    helperText={errors.telegram?.message}
                  />
                )}
              />
              <Controller
                name="telegram_group_id"
                control={control}
                render={({ field }: { field: any }) => (
                  <TextField
                    {...field}
                    label="Telegram Group ID"
                    fullWidth
                    placeholder="Group ID from bot setup"
                    helperText="Get this from the Telegram bot /setup command"
                  />
                )}
              />
            </Box>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Controller
                name="contract_address"
                control={control}
                rules={{
                  minLength: {
                    value: 20,
                    message: "Contract address must be at least 20 characters long"
                  },
                  validate: (value: string | undefined) => {
                    const selectedChain = getValues('chain');
                    
                    if (!value) return true; // Allow empty (optional field)
                    
                    // Ethereum format: 0x followed by 40 hex characters
                    if (selectedChain === 'ETH' || selectedChain === 'BNB' || selectedChain === 'BASE' || 
                        selectedChain === 'ARBITRUM' || selectedChain === 'POLYGON' || selectedChain === 'AVALANCHE') {
                      const ethRegex = /^0x[a-fA-F0-9]{40}$/;
                      if (!ethRegex.test(value)) {
                        return "Please enter a valid Ethereum contract address (0x followed by 40 hex characters)";
                      }
                    }
                    
                    // Solana format: Base58 encoded string (typically 32-44 characters)
                    if (selectedChain === 'SOL') {
                      const solRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
                      if (!solRegex.test(value)) {
                        return "Please enter a valid Solana contract address (Base58 encoded, 32-44 characters)";
                      }
                    }
                    
                    return true;
                  }
                }}
                render={({ field }: { field: any }) => {
                  const selectedChain = getValues('chain');
                  const placeholder = selectedChain === 'SOL' ? 'Base58 address...' : '0x...';
                  
                  return (
                    <TextField
                      {...field}
                      label="Contract Address"
                      fullWidth
                      placeholder={placeholder}
                      error={!!errors.contract_address}
                      helperText={errors.contract_address?.message}
                    />
                  );
                }}
              />
              <Controller
                name="chain"
                control={control}
                render={({ field }: { field: any }) => (
                  <TextField
                    {...field}
                    select
                    label="Chain"
                    fullWidth
                    SelectProps={{ native: true }}
                  >
                    <option value="ETH">Ethereum</option>
                    <option value="SOL">Solana</option>
                    <option value="BNB">BNB Smart Chain</option>
                    <option value="BASE">Base</option>
                    <option value="ARBITRUM">Arbitrum</option>
                    <option value="POLYGON">Polygon</option>
                    <option value="AVALANCHE">Avalanche</option>
                  </TextField>
                )}
              />
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Features
              </Typography>
              <FormGroup>
                <Controller
                  name="features.chat"
                  control={control}
                  render={({ field }: { field: any }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Enable Chat (Human Support)"
                    />
                  )}
                />
                <Controller
                  name="features.ai_search"
                  control={control}
                  render={({ field }: { field: any }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Enable AI Web3 Search"
                    />
                  )}
                />
                <Controller
                  name="features.feedback"
                  control={control}
                  render={({ field }: { field: any }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Enable Feedback Collection"
                    />
                  )}
                />
              </FormGroup>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Admin Settings
              </Typography>
              <Controller
                name="is_super_project"
                control={control}
                render={({ field }: { field: any }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={true}
                      />
                    }
                    label="Super User Project (Access to all projects)"
                  />
                )}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                This setting cannot be changed
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 }
            }}
          >
            {loading ? "Saving..." : project ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectForm;
